import { PlusIcon, Trash2Icon } from 'lucide-react';
import { Controller, useFieldArray, useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TEASER_MAX_QUICK_REPLIES } from '@/shared/types/teaser';

import { FieldValue, TeaserFieldsPrefix, TeaserHostForm } from './types';

type Props = {
  prefix: TeaserFieldsPrefix;
  readOnly?: boolean;
  /**
   * Whether an empty list is an error.
   *
   * True at block level, where `question_chips` with nothing to offer is an
   * incomplete state the serialiser silently drops. False for a section, where
   * "no actions here" is a real answer — the design shows exactly that, a
   * generated section the author left empty — and the section is simply not
   * saved.
   */
  required?: boolean;
};

/**
 * The tappable questions a `question_chips` teaser offers.
 *
 * Held as `{ value }[]` rather than `string[]`: `useFieldArray` over primitives
 * replaces the whole array on every keystroke, and the input loses focus
 * because RHF keys on object identity.
 *
 * The cap is enforced twice — the add button disables at the limit, and
 * `toTeaserBlock` slices — because the array can also arrive over the cap from
 * the generator.
 */
const QuickReplies = ({ prefix, readOnly, required = false }: Props) => {
  const { t } = useTranslation();
  const { control, getFieldState } = useFormContext<TeaserHostForm>();
  // Own subscription — see the note in Teaser.tsx.
  const formState = useFormState({ control, name: `${prefix}.quick_replies` });
  const { fields, append, remove } = useFieldArray({
    control,
    name: `${prefix}.quick_replies`,
    rules: {
      /**
       * Chips with nothing to offer is the one incomplete state the serialiser
       * silently drops, so the author has to be told rather than watch their
       * teaser quietly not save.
       *
       * This rule is why switching variant has to `clearErrors`: the list
       * unmounts when another variant is picked but the error it left behind
       * would keep blocking `handleSubmit`, with nothing on screen to explain
       * why.
       */
      validate: (items: FieldValue[]) =>
        !required ||
        items?.some((item) => (item?.value ?? '').trim()) ||
        t('common.required_field'),
    },
  });

  const atCap = fields.length >= TEASER_MAX_QUICK_REPLIES;
  // Read through `formState`, not `errors`: RHF mutates `errors` in place, so the React
  // Compiler would cache the first answer. `root` is where RHF puts a field-array-level error.
  const listError = getFieldState(`${prefix}.quick_replies`, formState).error?.root?.message;

  return (
    <div className="flex flex-col gap-2">
      {fields.length > 0 && (
        <Label className="text-sm leading-5 font-medium">
          {t('integrations.web_teaser.quick_replies.title')}
        </Label>
      )}

      {fields.map((reply, index) => (
        <div key={reply.id} className="flex items-center gap-2">
          <Controller
            control={control}
            name={`${prefix}.quick_replies.${index}.value`}
            render={({ field }) => (
              <Input
                placeholder={t('integrations.web_teaser.quick_replies.item_placeholder')}
                readOnly={readOnly}
                {...field}
                value={field.value ?? ''}
              />
            )}
          />
          {!readOnly && (
            <Button
              className="hover:text-destructive hover:[&_svg]:text-destructive"
              type="button"
              variant="ghost-neutral"
              size="icon-sm"
              aria-label={t('common.delete')}
              onClick={() => remove(index)}
            >
              <Trash2Icon size={16} />
            </Button>
          )}
        </div>
      ))}

      {listError && <p className="text-sm leading-5 text-warning">{listError}</p>}

      {!readOnly && (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={atCap}
            onClick={() => append({ value: '' })}
          >
            <PlusIcon size={16} />
            {t('integrations.web_teaser.quick_replies.add')}
          </Button>
          {atCap && (
            <span className="text-sm leading-5 text-foreground-muted">
              {t('integrations.web_teaser.quick_replies.max', { 0: TEASER_MAX_QUICK_REPLIES })}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default QuickReplies;
